import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Link,
  Switch,
  FormControlLabel,
  Avatar,
  Chip,
  IconButton,
  Stack,
  Divider,
} from "@mui/material";
import { TransportDriver } from "types/TransportDriver";
import TransportDriverService from "services/TransportDriverService";
import dayjs from "dayjs";
import { Phone, WhatsApp } from "@mui/icons-material";
import toast from "react-hot-toast";

interface TransportDriversListProps {
  drivers: TransportDriver[];
}

const generateWhatsAppLink = (phone: string, message: string) =>
  `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;

// Vehicle type icon component
const VehicleTypeIcon = ({ vehicleType }: { vehicleType: string | null }) => {
  if (!vehicleType) return null;

  return (
    <Box
      component="img"
      src={vehicleType}
      alt="Vehicle type"
      sx={{
        width: 16,
        height: 16,
        objectFit: "contain",
        filter: "grayscale(100%)",
        opacity: 0.7,
      }}
    />
  );
};

const TransportDriversList: React.FC<TransportDriversListProps> = ({ drivers }) => {
  const [toggleActivityMutation] = TransportDriverService.useToggleTransportDriverActivityMutation();
  const [loadingDrivers, setLoadingDrivers] = useState<Set<number>>(new Set());

  const handleToggleActivity = async (driver: TransportDriver) => {
    if (loadingDrivers.has(driver.id)) return;

    setLoadingDrivers((prev) => new Set(prev).add(driver.id));

    try {
      await toggleActivityMutation({
        currentVehicleId: driver.currentVehicleId,
        transportDriverId: driver.id,
      }).unwrap();

      toast.success(`Actividad ${driver.isActive ? "desactivada" : "activada"} exitosamente`);
    } catch (error: any) {
      toast.error(error.message || "Error al cambiar estado de actividad");
    } finally {
      setLoadingDrivers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(driver.id);
        return newSet;
      });
    }
  };

  return (
    <Box
      display="grid"
      gridTemplateColumns={{
        xs: "1fr",
        sm: "repeat(2, 1fr)",
        md: "repeat(3, 1fr)",
      }}
      gap={2}
      sx={{ mt: 2 }}
    >
      {drivers.map((driver) => (
        <Card
          key={driver.id}
          variant="outlined"
          sx={{
            borderRadius: 2,
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: 3,
            },
          }}
        >
          <CardContent sx={{ p: 2 }}>
            {/* Header with Avatar and Status */}
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: driver.isActive ? "success.main" : "warning.main",
                  fontSize: "1.2rem",
                }}
              >
                {driver.user.name.charAt(0).toUpperCase()}
              </Avatar>

              <Box flex={1}>
                <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
                  {driver.user.name}
                </Typography>
                <Chip
                  label={driver.isActive ? "Activo" : "Inactivo"}
                  color={driver.isActive ? "success" : "warning"}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Stack>

            {/* Activity Toggle */}
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={driver.isActive}
                    onChange={() => handleToggleActivity(driver)}
                    disabled={loadingDrivers.has(driver.id)}
                    color="success"
                  />
                }
                label="Estado de actividad"
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "0.875rem",
                    color: "text.secondary",
                  },
                }}
              />
            </Box>

            <Divider sx={{ my: 1.5 }} />

            {/* Driver Information */}
            <Stack spacing={1.5}>
              {/* Last Connection */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Última conexión
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {dayjs(driver.lastConnectionDate).format("MMM D, YYYY h:mm A")}
                </Typography>
              </Box>

              {/* Vehicle Info */}
              {driver.currentVehicle && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Vehículo actual
                  </Typography>
                  <Stack direction="column" spacing={0.5}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <VehicleTypeIcon vehicleType={driver.currentVehicle.vehicleType?.icon || null} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {driver.currentVehicle.plate}
                      </Typography>
                    </Stack>

                    {driver.currentVehicle.vehicleType && (
                      <Typography variant="caption" color="text.secondary">
                        {driver.currentVehicle.vehicleType.type}
                      </Typography>
                    )}

                    <Stack direction="row" spacing={1}>
                      <Typography variant="caption" color="text.secondary">
                        {driver.currentVehicle.model}
                      </Typography>
                      {driver.currentVehicle.year && (
                        <>
                          <Typography variant="caption" color="text.secondary">
                            •
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {driver.currentVehicle.year}
                          </Typography>
                        </>
                      )}
                      {driver.currentVehicle.color && (
                        <>
                          <Typography variant="caption" color="text.secondary">
                            •
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {driver.currentVehicle.color}
                          </Typography>
                        </>
                      )}
                    </Stack>

                    {driver.currentVehicle.vehicleType?.maxPassengers && (
                      <Typography variant="caption" color="text.secondary">
                        Máx. {driver.currentVehicle.vehicleType.maxPassengers} pasajeros
                      </Typography>
                    )}
                  </Stack>
                </Box>
              )}

              {/* Contact Actions */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Contacto
                </Typography>
                <Stack direction="row" spacing={1}>
                  <IconButton
                    component={Link}
                    href={`tel:+503${driver.user.phone}`}
                    size="small"
                    sx={{
                      bgcolor: "primary.main",
                      color: "white",
                      "&:hover": { bgcolor: "primary.dark" },
                    }}
                  >
                    <Phone sx={{ fontSize: 16 }} />
                  </IconButton>

                  <IconButton
                    component={Link}
                    href={generateWhatsAppLink(
                      `+503${driver.user.phone}`,
                      `Hola ${driver.user.name}, tienes una solicitud de viaje pendiente en la App El Chalet.`,
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{
                      bgcolor: "#25D366",
                      color: "white",
                      "&:hover": { bgcolor: "#1da851" },
                    }}
                  >
                    <WhatsApp sx={{ fontSize: 16 }} />
                  </IconButton>

                  <Typography variant="body2" sx={{ ml: 1, alignSelf: "center" }}>
                    {driver.user.phone}
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default TransportDriversList;
